import { toaster } from '@/core/ui';
import {
  DeleteQuestionRecordReportsDocument,
  UpdateQuestionRecordReportDocument,
} from '@/graphql/gql/graphql';
import { gql, useMutation as useApolloMutation } from '@/libs/apollo/export';

export const useReportMutations = () => {
  const [updateRecordReportMutation, { loading: updateLoading }] = useApolloMutation(
    UpdateQuestionRecordReportDocument,
    {
      update(cache, _, { variables }) {
        if (!variables) return;
        const { id, input } = variables;

        const reportId = cache.identify({ __typename: 'RecordReport', id });
        if (!reportId) return;

        if (input.comment !== undefined) {
          cache.writeFragment({
            id: reportId,
            fragment: gql`
              fragment UpdatedComment on RecordReport {
                comment
              }
            `,
            data: { comment: input.comment },
          });
        }

        if (input.actionedBy !== undefined) {
          cache.writeFragment({
            id: reportId,
            fragment: gql`
              fragment UpdatedActionedBy on RecordReport {
                actionedBy
              }
            `,
            data: { actionedBy: input.actionedBy },
          });
        }

        // if (input.reason !== undefined) {
        //   cache.writeFragment({
        //     id: reportId,
        //     fragment: gql`
        //       fragment UpdatedReason on RecordReport {
        //         reason
        //       }
        //     `,
        //     data: { reason: input.reason },
        //   });
        // }

        if (input.state !== undefined) {
          cache.writeFragment({
            id: reportId,
            fragment: gql`
              fragment UpdatedState on RecordReport {
                state
              }
            `,
            data: { state: input.state },
          });
        }

        if (input.members !== undefined) {
          cache.writeFragment({
            id: reportId,
            fragment: gql`
              fragment UpdatedMembers on RecordReport {
                members {
                  userId
                  role
                  user {
                    id
                    firstName
                    displayName
                    username
                  }
                }
              }
            `,
            data: { members: input.members },
          });
        }
      },
    },
  );

  const [deleteRecordReportsMutation, { loading: deleteLoading }] = useApolloMutation(
    DeleteQuestionRecordReportsDocument,
    {
      update(cache, _, { variables }) {
        if (!variables) return;
        const { ids = [] } = variables as { ids: string[] };
        if (ids.length === 0) return;

        ids.forEach((id) => {
          cache.evict({
            id: cache.identify({ __typename: 'RecordReport', id }),
          });
        });
      },
    },
  );

  const handleStateChange = async (reportId: string, val: number | null) => {
    if (!reportId || val == null) return;

    try {
      await updateRecordReportMutation({
        variables: { id: reportId, input: { state: val } },
      });
      toaster.success('State updated');
    } catch (err: any) {
      toaster.error(err?.message || 'Failed to update state');
    }
  };

  const handleActionedByChange = async (reportId: string, val: number | null) => {
    if (!reportId || val == null) return;
    try {
      await updateRecordReportMutation({
        variables: { id: reportId, input: { actionedBy: val } },
      });
      toaster.success('Actioned by updated');
    } catch (err: any) {
      toaster.error(err?.message || 'Failed to update actioned by');
    }
  };

  const handleMembersChange = async (reportId: string, keys: any) => {
    if (!reportId || !keys) return;
    const currentIds = Array.from(keys) as (string | number)[];

    try {
      await updateRecordReportMutation({
        variables: {
          id: String(reportId),
          input: {
            members: currentIds.map((userId) => ({ userId: Number(userId), role: 1 })),
          },
        },
      });
      toaster.success('Members updated');
    } catch (err: any) {
      toaster.error(err?.message || 'Failed to update members');
    }
  };

  const handleCommentSave = async (reportId: string, comment: string) => {
    if (!reportId) return;
    try {
      await updateRecordReportMutation({
        variables: {
          id: reportId,
          input: { comment },
        },
      });
      toaster.success('Comment updated');
    } catch (err: any) {
      toaster.error(err?.message || 'Failed to update comment');
    }
  };

  const handleDelete = async (reportId: string | number) => {
    try {
      const res = await deleteRecordReportsMutation({
        variables: { ids: [String(reportId)] },
      });
      if (res.data?.restricted?.deleteRecordReports) {
        toaster.success('Report deleted');
      } else {
        toaster.error('Failed to delete report');
      }
    } catch (err: any) {
      toaster.error(err?.message || 'Failed to delete report');
    }
  };

  return {
    updateRecordReportMutation,
    deleteRecordReportsMutation,
    updateLoading,
    deleteLoading,
    handleStateChange,
    handleActionedByChange,
    handleMembersChange,
    handleCommentSave,
    handleDelete,
  };
};
